/**
 * CPF/CNPJ helpers.
 *
 * CNPJ supports the alphanumeric format issued by the Receita Federal since
 * 2026-07-01: the first 12 positions may be letters (A–Z) or digits (0–9) and
 * the two check digits remain numeric. Legacy all-numeric CNPJs stay valid.
 */

export const onlyAlphanumeric = (value) =>
    (value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

export const onlyDigits = (value) => (value || '').replace(/\D/g, '');

const applyMask = (chars, separators) => {
    let result = '';
    for (let i = 0; i < chars.length; i++) {
        if (separators[i]) result += separators[i];
        result += chars[i];
    }
    return result;
};

// 000.000.000-00
const maskCpf = (value) => applyMask(onlyDigits(value).slice(0, 11), { 3: '.', 6: '.', 9: '-' });

// XX.XXX.XXX/XXXX-XX (alphanumeric)
const maskCnpj = (value) =>
    applyMask(onlyAlphanumeric(value).slice(0, 14), { 2: '.', 5: '.', 8: '/', 12: '-' });

/**
 * Masks a combined CPF/CNPJ value. While the value is numeric and up to 11
 * chars it is treated as a CPF; once it contains a letter or exceeds 11 chars
 * it is treated as a CNPJ.
 */
export const maskCpfCnpj = (value) => {
    const clean = onlyAlphanumeric(value);
    const isCpf = clean.length <= 11 && /^[0-9]*$/.test(clean);
    return isCpf ? maskCpf(clean) : maskCnpj(clean);
};

/**
 * Validates a Brazilian CNPJ, accepting both the new alphanumeric format and
 * legacy all-numeric CNPJs. The mod-11 check uses each character's value as
 * `charCode - 48` ('0'→0 … '9'→9, 'A'→17 … 'Z'→42).
 */
export const isValidCnpj = (raw) => {
    const d = onlyAlphanumeric(raw);
    // 14 chars, not all identical, and the two check digits must be numeric.
    if (d.length !== 14 || /^(.)\1+$/.test(d) || !/^\d{2}$/.test(d.slice(12))) return false;
    const charValue = (c) => c.charCodeAt(0) - 48;
    const calc = (chars, weights) => {
        const sum = weights.reduce((acc, w, i) => acc + charValue(chars[i]) * w, 0);
        const r = sum % 11;
        return r < 2 ? 0 : 11 - r;
    };
    const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    return calc(d, w1) === Number(d[12]) && calc(d, w2) === Number(d[13]);
};
